/**
 * @fileoverview Publish API Endpoints
 * @description API calls for publish/unpublish operations
 * @author MCHD Room Scheduler Team
 * @created 08-10-2025 10:41:19
 * @complies @tech-stack-documentation.mdc - React Query + Fetch API patterns
 */

import { TokenManager } from '../interceptors';

const API_BASE_URL = (import.meta as any).env?.VITE_API_URL || 'http://localhost:7991/api/v1';

// =============================================================================
// TYPES
// =============================================================================

export interface CoverageStatsParams {
  startDate: string;
  endDate?: string;
  buildingId?: string;
  floorId?: string;
}

export interface CoverageStatsResponse {
  success: boolean;
  data?: {
    total: number;
    published: number;
    draft: number;
    coverage: number;
    conflicts: number;
    byRoom: Array<{
      roomId: string;
      roomNumber: string;
      published: number;
      draft: number;
      conflicts: number;
      total: number;
      coverage: number;
    }>;
  };
  error?: {
    code: string;
    message: string;
    details?: any;
  };
}

export interface BulkPublishParams {
  date: string;
  scope?: 'day' | 'week' | 'month';
  buildingId?: string;
  floorId?: string;
  roomIds?: string[];
  options?: {
    forcePublish?: boolean;
    skipConflicts?: boolean;
  };
}

export interface BulkPublishResponse {
  success: boolean;
  data?: {
    publishedCount: number;
    scope?: string;
    dateRange?: {
      startDate: Date;
      endDate: Date;
    };
    byRoom?: Array<{
      roomNumber: string;
      published: number;
      total: number;
    }>;
    message?: string;
  };
  error?: {
    code: string;
    message: string;
    details?: any;
  };
}

// =============================================================================
// API UTILITIES
// =============================================================================

/**
 * Get authorization headers for API requests
 * @returns Headers object with authorization
 * 
 * @created 08-10-2025 10:41:19
 */
function getAuthHeaders(): HeadersInit {
  const token = TokenManager.getAccessToken();
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
}

/**
 * Make authenticated API request
 * @param url - API endpoint URL
 * @param options - Fetch options
 * @returns Promise with response data
 * 
 * @created 08-10-2025 10:41:19
 */
async function apiRequest<T>(url: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(url, {
    ...options,
    headers: {
      ...getAuthHeaders(),
      ...options.headers,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({
      error: {
        code: 'NETWORK_ERROR',
        message: 'Network request failed',
      },
    }));
    throw new Error(errorData.error?.message || 'API request failed');
  }

  return response.json();
}

/**
 * Build query string from parameters
 * @param params - Query parameters
 * @returns Query string
 * 
 * @created 08-10-2025 10:41:19
 */
function buildQueryString(params: Record<string, any>): string {
  const searchParams = new URLSearchParams();
  
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, String(value));
    }
  });
  
  return searchParams.toString();
}

// =============================================================================
// PUBLISH API FUNCTIONS
// =============================================================================

/**
 * Get coverage statistics for a date range
 * @param params - Coverage stats parameters
 * @returns Promise with coverage statistics
 * 
 * UX Spec: Section 3.6 - Show coverage statistics before publish
 * - Overall coverage %
 * - Published/Draft/Conflict counts
 * - Room-by-room breakdown
 * 
 * @created 08-10-2025 10:41:19
 */
export async function getCoverageStats(params: CoverageStatsParams): Promise<CoverageStatsResponse> {
  const queryString = buildQueryString(params);
  const url = `${API_BASE_URL}/publish/coverage${queryString ? `?${queryString}` : ''}`;
  
  return apiRequest<CoverageStatsResponse>(url);
}

/**
 * Bulk publish assignments
 * @param params - Bulk publish parameters
 * @returns Promise with publish result
 * 
 * UX Spec: Section 3.6 - Bulk publish with conflict handling
 * - Publish by date range/building/floor/rooms
 * - Handle conflicts (force or skip options)
 * - Return published count and breakdown
 * 
 * @created 08-10-2025 10:41:19
 */
export async function bulkPublishAssignments(params: BulkPublishParams): Promise<BulkPublishResponse> {
  const url = `${API_BASE_URL}/publish/bulk-publish`;
  
  return apiRequest<BulkPublishResponse>(url, {
    method: 'POST',
    body: JSON.stringify(params),
  });
}

/**
 * Bulk unpublish assignments
 * @param params - Bulk unpublish parameters
 * @returns Promise with unpublish result
 * 
 * @created 08-10-2025 10:41:19
 */
export async function bulkUnpublishAssignments(params: BulkPublishParams): Promise<BulkPublishResponse> {
  const url = `${API_BASE_URL}/publish/bulk-unpublish`;
  
  return apiRequest<BulkPublishResponse>(url, {
    method: 'POST',
    body: JSON.stringify(params),
  });
}

